/**
 * What the SERVICE members of a bundle cost — the one figure their own catalog
 * row cannot state.
 *
 * A material or equipment member answers the Cost column from its cost basis. A
 * service cannot: its cost is the TIME it takes plus what the parts it consumes
 * cost, and a dynamically-priced service is required to store `costBasis: 0`
 * because of exactly that. Reading the column reported a $250 repair and a $216
 * dynamic service as costing $0.00 each, and any bundle cost or margin built on
 * top of them inherited the same zero.
 *
 * The labour half is already on the item the caller holds. The parts half is
 * not — a catalog list row carries the member's own fields, never the
 * components hanging under it — so those are fetched here, one query per
 * service member, sharing react-query's cache with every other reader of the
 * same endpoint.
 */
import { useQueries } from '@tanstack/react-query';
import { useMemo } from 'react';
import {
  type PricebookComponentItemCache,
  type ServiceMemberCostBreakdowns,
  type ServiceMemberCosts,
  serviceOwnCostBreakdown,
  type ServiceOwnPart,
} from '@/components/organisms/pricebook/support/pricebook-bundle';
import { apiGet } from '@/lib/api';
import { pricebookKeys } from '@/lib/queryKeys';
import type { PricingTreatment } from '@/lib/sales/service-rollup';
import type { ServiceComponent } from '@/types/pricebook';
import { useLaborRateProfiles } from './useDynamicPricing';
import { usePricebookApi } from './usePricebookApi';
import { mapOperationsLineItem } from './usePricebookItems';

/**
 * `/components` answers in TWO shapes — a bare array, and a `{ data }`
 * envelope — and both are live. `LineItemPicker.fetchComponents()` already
 * normalizes for exactly that reason; reading only `.data.data` here would
 * leave every service member unresolved against the bare-array form, and the
 * Cost column would print "not known yet" forever rather than a real figure.
 */
export type ServiceMemberPart = ServiceOwnPart & {
  id: string;
  name: string;
  code?: string | null;
  unit?: string | null;
  basePrice?: number | null;
  price?: number | null;
  pricingTreatment?: PricingTreatment | null;
};

export interface ServiceMemberBreakdown {
  cost: ServiceMemberCostBreakdowns[string];
  parts: ServiceMemberPart[];
  /** Full catalog record; list/picker rows may omit the service's labor configuration. */
  service: PricebookComponentItemCache[string];
}

export type ServiceMemberBreakdowns = Record<string, ServiceMemberBreakdown>;

type ComponentsResponse = ServiceMemberPart[] | { data?: ServiceMemberPart[] };

function unwrapComponents(raw: ComponentsResponse | undefined): ServiceMemberPart[] | undefined {
  if (Array.isArray(raw)) return raw;
  return raw?.data;
}

export function serviceMemberCostsFromBreakdowns(
  breakdowns: ServiceMemberBreakdowns,
): ServiceMemberCosts {
  return Object.fromEntries(
    Object.entries(breakdowns).map(([id, breakdown]) => [id, breakdown.cost.total]),
  );
}

export function serviceMemberCostBreakdownsFrom(
  breakdowns: ServiceMemberBreakdowns,
): ServiceMemberCostBreakdowns {
  return Object.fromEntries(
    Object.entries(breakdowns).map(([id, breakdown]) => [id, breakdown.cost]),
  );
}

export function useServiceMemberBreakdowns(
  children: ServiceComponent[],
  itemCache: PricebookComponentItemCache,
): ServiceMemberBreakdowns {
  const { client, ep } = usePricebookApi();
  const { data: laborProfiles = [] } = useLaborRateProfiles();

  // Only members that have resolved to a SERVICE, de-duplicated: a bundle may
  // legitimately hold the same service twice, and that is one fetch.
  const serviceIds = useMemo(() => {
    const seen = new Set<string>();
    for (const child of children) {
      if (itemCache[child.itemId]?.type === 'service') seen.add(child.itemId);
    }
    return [...seen].sort();
  }, [children, itemCache]);

  const results = useQueries({
    queries: serviceIds.map((id) => ({
      queryKey: pricebookKeys.components(id),
      queryFn: () => apiGet<ComponentsResponse>(client, ep.components(id)),
    })),
  });

  // A picker result is intentionally compact and is not guaranteed to carry
  // dynamicPricingConfig/laborCostLines. Costing from it can therefore show a
  // complete service price beside $0 labor. Resolve the full service record
  // before publishing a breakdown so a loading state is never mistaken for a
  // genuinely labor-free service.
  const detailResults = useQueries({
    queries: serviceIds.map((id) => ({
      queryKey: pricebookKeys.detail(id),
      queryFn: async () =>
        mapOperationsLineItem(await apiGet<Record<string, unknown>>(client, ep.detail(id))),
    })),
  });

  const breakdowns: ServiceMemberBreakdowns = {};
  serviceIds.forEach((id, index) => {
    const parts = unwrapComponents(results[index]?.data);
    const service = detailResults[index]?.data;
    // Absent until the fetch lands. Leaving the id out is what lets the Cost
    // column print "not known yet" rather than a zero that reads as free. Both
    // requests are required: parts answer materials/equipment and the detail
    // record answers labor.
    if (!parts || !service) return;
    breakdowns[id] = {
      cost: serviceOwnCostBreakdown(service, parts, laborProfiles),
      parts,
      service,
    };
  });

  // `useQueries` hands back new result objects on every render, so the map is
  // rebuilt every time and would be a new identity every time — which is enough
  // to re-run every total memoized on it, forever. Its VALUES are what callers
  // depend on, so the identity is pinned to those.
  const signature = JSON.stringify(breakdowns);
  return useMemo(() => JSON.parse(signature) as ServiceMemberBreakdowns, [signature]);
}

export function useServiceMemberCosts(
  children: ServiceComponent[],
  itemCache: PricebookComponentItemCache,
): ServiceMemberCosts {
  return serviceMemberCostsFromBreakdowns(useServiceMemberBreakdowns(children, itemCache));
}
